import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Toggle } from 'components/Toggle';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { GET_RETURN_PRODUCT_ELIGIBILITY_CATALOG } from 'gql/queries';
import { GET_RETURN_INCENTIVE_COLLECTIONS, GET_RETURN_INCENTIVE_PRODUCTS } from 'gql/incentiveCatalog';
import { ReturnInspectionPolicy } from 'types/policy';
import { IncentiveCatalogReference } from 'types/incentive';
import { IncentiveCatalogPicker } from './IncentiveCatalogPicker';

export const defaultInspectionPolicy = (
  stored?: any,
): ReturnInspectionPolicy => ({
  enabled: stored?.enabled === true && stored?.mode !== 'NEVER',
  mode:
    stored?.enabled !== true
      ? 'NEVER'
      : stored?.mode === 'MATCHING'
      ? 'MATCHING'
      : stored?.mode === 'NEVER'
      ? 'NEVER'
      : 'ALWAYS',
  rules: {
    products: {
      enabled: false,
      ...stored?.rules?.products,
      productIds: Array.isArray(stored?.rules?.products?.productIds) ? stored.rules.products.productIds : [],
      collectionIds: Array.isArray(stored?.rules?.products?.collectionIds) ? stored.rules.products.collectionIds : [],
      tags: Array.isArray(stored?.rules?.products?.tags) ? stored.rules.products.tags : [],
    },
    itemQuantity: {
      enabled: false,
      minimum: 4,
      ...stored?.rules?.itemQuantity,
    },
    returnValue: {
      enabled: false,
      minimum: 100,
      ...stored?.rules?.returnValue,
    },
    reasons: { enabled: false, ...stored?.rules?.reasons, reasonIds: Array.isArray(stored?.rules?.reasons?.reasonIds) ? stored.rules.reasons.reasonIds : [] },
    customerHistory: {
      enabled: false,
      minimumReturns: 3,
      lookbackDays: 365,
      ...stored?.rules?.customerHistory,
    },
    risk: { enabled: false, minimumScore: 80, ...stored?.rules?.risk },
  },
});

export const getInspectionPolicyError = (value: ReturnInspectionPolicy) => {
  if (!value.enabled || value.mode !== 'MATCHING') return null;
  const rules = value.rules;
  if (!Object.values(rules).some((rule) => rule.enabled))
    return 'Enable at least one inspection rule.';
  if (
    rules.products.enabled &&
    ![
      ...rules.products.productIds,
      ...rules.products.collectionIds,
      ...rules.products.tags,
    ].length
  )
    return 'Choose a product, collection or tag for inspection.';
  if (rules.reasons.enabled && !rules.reasons.reasonIds.length)
    return 'Choose at least one inspection reason.';
  const integer = (value: number, max: number) =>
    Number.isInteger(value) && value >= 1 && value <= max;
  if (rules.itemQuantity.enabled && !integer(rules.itemQuantity.minimum, 10000))
    return 'Item quantity must be a whole number from 1 to 10,000.';
  if (
    rules.returnValue.enabled &&
    (!Number.isFinite(rules.returnValue.minimum) ||
      rules.returnValue.minimum < 0 ||
      rules.returnValue.minimum > 1e9)
  )
    return 'Enter a valid minimum return value.';
  if (
    rules.customerHistory.enabled &&
    (!integer(rules.customerHistory.minimumReturns, 10000) ||
      !integer(rules.customerHistory.lookbackDays, 3650))
  )
    return 'Enter valid prior returns and a history window from 1 to 3,650 days.';
  if (
    rules.risk.enabled &&
    (!Number.isFinite(rules.risk.minimumScore) ||
      rules.risk.minimumScore < 0 ||
      rules.risk.minimumScore > 100)
  )
    return 'Risk score must be from 0 to 100.';
  return null;
};

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const ruleRowClass = 'px-4 py-4 sm:px-5';
const numericRuleRowClass = `${ruleRowClass} grid gap-3 sm:grid-cols-[minmax(0,1fr)_15rem] sm:items-center sm:gap-6`;
const validInteger = (number: number, max: number) =>
  Number.isInteger(number) && number >= 1 && number <= max;
const validAmount = (number: number, max: number) =>
  Number.isFinite(number) && number >= 0 && number <= max;
type CatalogResource = 'products' | 'collections';
const catalogIdSearch = (ids: string[], resource: CatalogResource) => ids.map(id => {
  const match = typeof id === 'string' && id.match(new RegExp(`^gid://shopify/${resource === 'products' ? 'Product' : 'Collection'}/(\\d+)$`));
  const term = match ? `id:${match[1]}` : '';
  return term.length <= 255 ? term : '';
}).filter(Boolean).join(' OR ');
const catalogIdGroups = (ids: string[], resource: CatalogResource) => {
  const groups: string[][] = [];
  ids.forEach(id => {
    const current = groups[groups.length - 1];
    if (!current || current.length >= 50 || catalogIdSearch([...current, id], resource).length > 255) groups.push([id]);
    else current.push(id);
  });
  return groups;
};
function SavedInspectionCatalogReferences({storeId, resource, ids, references, onRemove, onResolve}: {
  storeId?: string;
  resource: CatalogResource;
  ids: string[];
  references: Record<string, string>;
  onRemove: (id: string) => void;
  onResolve: (references: Record<string, string>) => void;
}) {
  const search = catalogIdSearch(ids.filter(id => !references[id]), resource);
  const resultKey = resource === 'products' ? 'returnIncentiveProducts' : 'returnIncentiveCollections';
  const {data, loading, error, refetch} = useQuery(resource === 'products' ? GET_RETURN_INCENTIVE_PRODUCTS : GET_RETURN_INCENTIVE_COLLECTIONS, {
    variables: {input: {storeId, search}},
    skip: !storeId || !search,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });
  const nodes: IncentiveCatalogReference[] = !loading && !error ? data?.[resultKey]?.nodes || [] : [];
  useEffect(() => {
    if (loading || error || !search) return;
    const resolved: IncentiveCatalogReference[] = data?.[resultKey]?.nodes || [];
    const selected = resolved.filter(node => ids.includes(node.id) && !references[node.id] && typeof node.title === 'string' && node.title.trim());
    if (selected.length) onResolve(Object.fromEntries(selected.map(node => [node.id, node.title])));
  }, [data, resultKey, loading, error, search, ids, references, onResolve]);
  const singular = resource === 'products' ? 'product' : 'collection';
  return <div className="space-y-2">
    <div className="flex flex-wrap gap-2">
      {ids.map(id => {
        const name = references[id] || nodes.find(node => node.id === id)?.title;
        const identity = typeof id === 'string' ? id.split('/').pop() : 'unknown';
        const label = name || `${loading ? `Loading ${singular}…` : `${singular === 'product' ? 'Product' : 'Collection'} name unavailable`} (${identity})`;
        return <button key={id} type="button" title={id} aria-label={`Remove ${label}`} onClick={() => onRemove(id)} className="rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-800">{label} ×</button>;
      })}
    </div>
    {error && search && <p role="alert" className="text-xs text-red-700">
      Saved {singular} names could not be loaded.{' '}
      <button type="button" disabled={loading} onClick={() => refetch()}>{loading ? 'Retrying names…' : `Retry ${singular} names`}</button>
    </p>}
  </div>;
}
type Props = {
  storeId?: string;
  value: ReturnInspectionPolicy;
  onChange: (value: ReturnInspectionPolicy) => void;
  reasons: { id: string; label: string }[];
  unverified?: boolean;
};

export function InspectionPolicyControls({
  storeId,
  value,
  onChange,
  reasons,
  unverified = false,
}: Props) {
  const [picker, setPicker] = useState<'products' | 'collections' | null>(null);
  const [catalogReferences, setCatalogReferences] = useState<Record<string, Record<string, string>>>({});
  const references = storeId ? catalogReferences[storeId] || {} : {};
  const rememberResolvedReferences = useCallback((resolved: Record<string, string>) => {
    if (!storeId) return;
    setCatalogReferences(current => {
      const labels = current[storeId] || {};
      if (Object.entries(resolved).every(([id, title]) => labels[id] === title)) return current;
      return {...current, [storeId]: {...labels, ...resolved}};
    });
  }, [storeId]);
  const lastValid = useRef({
    quantity: 4,
    value: 100,
    returns: 3,
    days: 365,
    risk: 80,
  });
  useEffect(() => {
    if (validInteger(value.rules.itemQuantity.minimum, 10000))
      lastValid.current.quantity = value.rules.itemQuantity.minimum;
    if (validAmount(value.rules.returnValue.minimum, 1e9))
      lastValid.current.value = value.rules.returnValue.minimum;
    if (validInteger(value.rules.customerHistory.minimumReturns, 10000))
      lastValid.current.returns = value.rules.customerHistory.minimumReturns;
    if (validInteger(value.rules.customerHistory.lookbackDays, 3650))
      lastValid.current.days = value.rules.customerHistory.lookbackDays;
    if (validAmount(value.rules.risk.minimumScore, 100))
      lastValid.current.risk = value.rules.risk.minimumScore;
  }, [value.rules.itemQuantity.minimum, value.rules.returnValue.minimum, value.rules.customerHistory.minimumReturns, value.rules.customerHistory.lookbackDays, value.rules.risk.minimumScore]);
  const inactiveRules = (): ReturnInspectionPolicy['rules'] => ({
    ...value.rules,
    products: {
      ...value.rules.products,
      enabled:
        value.rules.products.enabled &&
        [
          ...value.rules.products.productIds,
          ...value.rules.products.collectionIds,
          ...value.rules.products.tags,
        ].length > 0,
    },
    reasons: {
      ...value.rules.reasons,
      enabled:
        value.rules.reasons.enabled && value.rules.reasons.reasonIds.length > 0,
    },
    itemQuantity: {
      ...value.rules.itemQuantity,
      minimum: lastValid.current.quantity,
    },
    returnValue: {
      ...value.rules.returnValue,
      minimum: lastValid.current.value,
    },
    customerHistory: {
      ...value.rules.customerHistory,
      minimumReturns: lastValid.current.returns,
      lookbackDays: lastValid.current.days,
    },
    risk: { ...value.rules.risk, minimumScore: lastValid.current.risk },
  });
  const { data, loading, error, refetch } = useQuery(
    GET_RETURN_PRODUCT_ELIGIBILITY_CATALOG,
    {
      variables: { input: { storeId } },
      skip:
        !storeId || value.mode !== 'MATCHING' || !value.rules.products.enabled,
      fetchPolicy: 'network-only',
    },
  );
  const update = <K extends keyof ReturnInspectionPolicy['rules']>(
    key: K,
    changes: Partial<ReturnInspectionPolicy['rules'][K]>,
  ) =>
    onChange({
      ...value,
      rules: { ...value.rules, [key]: { ...value.rules[key], ...changes } },
    });
  const toggle = (
    key: keyof ReturnInspectionPolicy['rules'],
    label: string,
  ) => (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-gray-900">{label}</span>
      <Toggle
        ariaLabel={label}
        isEnabled={value.rules[key].enabled}
        onChange={(enabled) =>
          onChange({
            ...value,
            rules: {
              ...value.rules,
              [key]: {
                ...(enabled ? value.rules[key] : inactiveRules()[key]),
                enabled,
              },
            },
          })
        }
      />
    </div>
  );
  const number = (
    label: string,
    current: number,
    change: (number: number) => void,
    max = 10000,
    min = 1,
  ) => (
    <label className="block text-xs font-medium text-gray-600">
      {label}
      <NonNegativeNumberInput
        min={min}
        max={max}
        step={1}
        value={Number.isFinite(current) ? current : ''}
        onChange={(event) =>
          change(
            event.target.value === '' ? Number.NaN : Number(event.target.value),
          )
        }
        className={inputClass}
      />
    </label>
  );
  const tags: { id: string; label: string }[] =
    data?.returnProductEligibilityCatalog?.tags || [];
  const selectValues = (values: string[], selected: string) =>
    values.includes(selected)
      ? values.filter((selectedId) => selectedId !== selected)
      : [...values, selected];
  return (
    <section aria-label="Return inspection" className="space-y-5">
      <fieldset className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <legend className="px-1 text-sm font-semibold text-gray-900">
          Require inspection
        </legend>
        <div className="flex w-full rounded-xl bg-gray-100 p-1 sm:max-w-xl">
          {[
            { mode: 'NEVER', label: 'Off' },
            { mode: 'ALWAYS', label: 'All returns' },
            { mode: 'MATCHING', label: 'Matching returns' },
          ].map((option) => (
            <label
              key={option.mode}
              className={`relative flex flex-1 cursor-pointer items-center justify-center rounded-lg border px-2 py-2.5 text-center text-sm font-medium transition focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 sm:px-4 ${
                !unverified && value.mode === option.mode
                  ? 'border-blue-600 bg-white text-blue-800 shadow-sm'
                  : 'border-transparent text-gray-600 hover:bg-white hover:text-gray-900'
              }`}
            >
              <input
                type="radio"
                className="sr-only"
                name="inspection-mode"
                checked={!unverified && value.mode === option.mode}
                onChange={() =>
                  onChange({
                    ...value,
                    enabled: option.mode !== 'NEVER',
                    mode: option.mode as ReturnInspectionPolicy['mode'],
                    rules:
                      option.mode === 'MATCHING'
                        ? value.rules
                        : inactiveRules(),
                  })
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
      {value.mode === 'MATCHING' && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-5">
            <h4 className="text-sm font-semibold text-gray-900">
              Inspection rules
            </h4>
            <p className="mt-1 text-xs text-gray-500">
              Require inspection when any enabled rule matches.
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            <div className={ruleRowClass}>
              {toggle('products', 'Products, collections or tags')}
              {value.rules.products.enabled && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-md border px-3 py-2 text-sm"
                      onClick={() => setPicker('products')}
                    >
                      Choose products
                    </button>
                    <button
                      type="button"
                      className="rounded-md border px-3 py-2 text-sm"
                      onClick={() => setPicker('collections')}
                    >
                      Choose collections
                    </button>
                  </div>
                  {(['productIds', 'collectionIds'] as const).map((key) => (
                    <div key={key} className="flex flex-wrap gap-2">
                      {catalogIdGroups(value.rules.products[key], key === 'productIds' ? 'products' : 'collections').map((ids, index) => (
                        <SavedInspectionCatalogReferences
                          key={`${storeId}:${index}:${ids.join(',')}`}
                          storeId={storeId}
                          resource={key === 'productIds' ? 'products' : 'collections'}
                          ids={ids}
                          references={references}
                          onResolve={rememberResolvedReferences}
                          onRemove={id =>
                            update('products', {
                              [key]: value.rules.products[key].filter(
                                (selectedId) => selectedId !== id,
                              ),
                            })
                          }
                        />
                      ))}
                    </div>
                  ))}
                  <fieldset>
                    <legend className="mb-2 text-xs font-medium text-gray-600">
                      Product tags
                    </legend>
                    {loading ? (
                      <p role="status" className="text-xs text-gray-500">
                        Loading tags…
                      </p>
                    ) : error ||
                      data?.returnProductEligibilityCatalog?.error ? (
                      <div role="alert" className="text-xs text-red-700">
                        Tags could not be loaded.{' '}
                        <button type="button" onClick={() => refetch()}>
                          Retry tags
                        </button>
                      </div>
                    ) : (
                      <div className="flex max-h-32 flex-wrap gap-3 overflow-auto">
                        {Array.from(
                          new Set([
                            ...tags.map((tag) => tag.id),
                            ...value.rules.products.tags,
                          ]),
                        ).map((tag) => (
                          <label
                            key={tag}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={value.rules.products.tags.includes(tag)}
                              onChange={() =>
                                update('products', {
                                  tags: selectValues(
                                    value.rules.products.tags,
                                    tag,
                                  ),
                                })
                              }
                            />
                            {tags.find((value) => value.id === tag)?.label ||
                              tag}
                          </label>
                        ))}
                        {!tags.length && !value.rules.products.tags.length && (
                          <p className="text-xs text-gray-500">
                            No product tags found.
                          </p>
                        )}
                      </div>
                    )}
                  </fieldset>
                </div>
              )}
            </div>
            <div className={numericRuleRowClass}>
              {toggle('itemQuantity', 'Item quantity')}
              {value.rules.itemQuantity.enabled && (
                <div>
                  {number(
                    'Minimum item quantity',
                    value.rules.itemQuantity.minimum,
                    (minimum) => update('itemQuantity', { minimum }),
                  )}
                </div>
              )}
            </div>
            <div className={numericRuleRowClass}>
              {toggle('returnValue', 'Return value')}
              {value.rules.returnValue.enabled && (
                <div>
                  {number(
                    'Minimum merchandise value (order currency)',
                    value.rules.returnValue.minimum,
                    (minimum) => update('returnValue', { minimum }),
                    1e9,
                    0,
                  )}
                </div>
              )}
            </div>
            <div className={ruleRowClass}>
              {toggle('reasons', 'Return reasons')}
              {value.rules.reasons.enabled && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {Array.from(
                    new Set([
                      ...reasons.map((reason) => reason.id),
                      ...value.rules.reasons.reasonIds,
                    ]),
                  ).map((id) => (
                    <label key={id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value.rules.reasons.reasonIds.includes(id)}
                        onChange={() =>
                          update('reasons', {
                            reasonIds: selectValues(
                              value.rules.reasons.reasonIds,
                              id,
                            ),
                          })
                        }
                      />
                      {reasons.find((reason) => reason.id === id)?.label || id}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className={numericRuleRowClass}>
              {toggle('customerHistory', 'Customer return history')}
              {value.rules.customerHistory.enabled && (
                <div className="grid grid-cols-2 items-end gap-3">
                  {number(
                    'Prior returns',
                    value.rules.customerHistory.minimumReturns,
                    (minimumReturns) =>
                      update('customerHistory', { minimumReturns }),
                  )}
                  {number(
                    'History window (days)',
                    value.rules.customerHistory.lookbackDays,
                    (lookbackDays) =>
                      update('customerHistory', { lookbackDays }),
                    3650,
                  )}
                </div>
              )}
            </div>
            <div className={numericRuleRowClass}>
              {toggle('risk', 'Risk score')}
              {value.rules.risk.enabled && (
                <div>
                  {number(
                    'Minimum risk score',
                    value.rules.risk.minimumScore,
                    (minimumScore) => update('risk', { minimumScore }),
                    100,
                    0,
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {picker && storeId && (
        <IncentiveCatalogPicker
          resource={picker}
          storeId={storeId}
          selected={(picker === 'products'
            ? value.rules.products.productIds
            : value.rules.products.collectionIds
          ).map((id) => ({ id, title: references[id] || id }))}
          onClose={() => setPicker(null)}
          onChange={(selected: IncentiveCatalogReference[]) => {
            setCatalogReferences((current) => ({
              ...current,
              [storeId]: {
                ...current[storeId],
                ...Object.fromEntries(
                  selected.filter(reference => reference.title !== reference.id).map((reference) => [reference.id, reference.title]),
                ),
              },
            }));
            update('products', {
              [picker === 'products' ? 'productIds' : 'collectionIds']:
                selected.map((reference) => reference.id),
            });
          }}
        />
      )}
      {getInspectionPolicyError(value) && (
        <p role="alert" className="text-sm text-red-700">
          {getInspectionPolicyError(value)}
        </p>
      )}
    </section>
  );
}
